/**
 * IDAAS platform RBAC permission catalog.
 *
 * Permission data is generated from
 * `servers/idaas/domain/src/access_control/catalog.rs::CATALOG` by
 * `just generate-idaas-catalog`. This module owns only the TypeScript brand and
 * query helpers used by frontend consumers.
 */

import { GENERATED_CATALOG, GENERATED_PERMISSIONS } from "./catalog.generated";

const PERMISSION_NAME_BRAND = Symbol("PermissionName");

/** Branded permission name sourced from the IDAAS domain catalog. */
export type PermissionName = string & {
  readonly [PERMISSION_NAME_BRAND]: typeof PERMISSION_NAME_BRAND;
};

/** A single generated catalog entry. */
export interface PermissionDef {
  readonly name: PermissionName;
  readonly resource: string;
  readonly action: string;
  readonly description: string;
}

/** Full catalog in the same stable order as the Rust source. */
export const CATALOG: readonly PermissionDef[] = GENERATED_CATALOG.map((permission) => ({
  ...permission,
  name: permission.name as PermissionName,
}));

type GeneratedPermissions = {
  readonly [Key in keyof typeof GENERATED_PERMISSIONS]: (typeof GENERATED_PERMISSIONS)[Key] & PermissionName;
};

/** Typed string constants keyed by `RESOURCE_ACTION`. */
export const PERMISSIONS = GENERATED_PERMISSIONS as GeneratedPermissions;

/** Lookup a permission definition by full name. */
export function lookup(name: string): PermissionDef | null {
  return CATALOG.find((permission) => permission.name === name) ?? null;
}

/** Return whether a string is a generated catalog permission. */
export function isPermissionName(name: string): name is PermissionName {
  return CATALOG.some((permission) => permission.name === name);
}

/** Numeric JWT wire discriminators from Rust `AuthorizationScope::kind()`. */
export const AUTHORIZATION_SCOPE_KIND = {
  REALM: 1,
  TENANT: 2,
  PLATFORM: 3,
} as const;

/** Canonical target UUID carried by platform-scoped JWT claims. */
export const PLATFORM_TENANT_ID = "00000000-0000-0000-0000-000000000100" as const;
